import type { RouterEventPayloads } from './registry/router.events';

type AllPayloads = RouterEventPayloads;

class EventBus<Events extends Record<string, any>> {
  private listeners = new Map<keyof Events, Array<(payload: Events[keyof Events]) => void>>();

  on<K extends keyof Events>(event: K, handler: (payload: Events[K]) => void): void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)!.push(handler as (payload: Events[keyof Events]) => void);
  }

  off<K extends keyof Events>(event: K, handler: (payload: Events[K]) => void): void {
    const handlers = this.listeners.get(event);
    if (!handlers) return;
    this.listeners.set(
      event,
      handlers.filter((h) => h !== handler),
    );
  }

  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const handlers = this.listeners.get(event);
    if (!handlers) return;
    for (const handler of handlers) {
      (handler as (payload: Events[K]) => void)(payload);
    }
  }

  once<K extends keyof Events>(event: K, handler: (payload: Events[K]) => void): void {
    const wrapper = (payload: Events[K]) => {
      handler(payload);
      this.off(event, wrapper);
    };
    this.on(event, wrapper);
  }

  clear(event?: keyof Events): void {
    if (event) {
      this.listeners.delete(event);
    } else {
      this.listeners.clear();
    }
  }
}

export const eventBus = new EventBus<AllPayloads>();
